import { Navigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/auth';
import { authQueryKeys } from '../hooks/auth/queryKeys';
import { LoadingSpinner } from '../components/LoadingSpinner';
import type { UserRole } from '../types/auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: UserRole | UserRole[];
  requireEmailVerified?: boolean;
}

export function ProtectedRoute({
  children,
  requiredRole,
  requireEmailVerified = true
}: ProtectedRouteProps) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, isLoading, isAuthenticated, needsRoleSelection } = useAuth();

  // МАКСИМАЛЬНОЕ ЛОГИРОВАНИЕ - начало каждого рендера
  console.log('[ProtectedRoute] ========== START ==========');
  console.log('[ProtectedRoute] Pathname:', location.pathname);
  console.log('[ProtectedRoute] Search:', location.search);
  console.log('[ProtectedRoute] requireEmailVerified:', requireEmailVerified);
  console.log('[ProtectedRoute] isLoading:', isLoading);
  console.log('[ProtectedRoute] isAuthenticated:', isAuthenticated);
  console.log('[ProtectedRoute] needsRoleSelection:', needsRoleSelection);
  console.log('[ProtectedRoute] User:', {
    email: user?.email,
    role: user?.role,
    emailVerified: user?.emailVerified,
    id: user?.id
  });

  if (isLoading) {
    console.log('[ProtectedRoute] RETURN: LoadingSpinner (isLoading=true)');
    return <LoadingSpinner />;
  }

  // Не авторизован
  if (!isAuthenticated) {
    console.log('[ProtectedRoute] RETURN: Navigate to /login (not authenticated)');
    return <Navigate to="/login" replace />;
  }

  // Требуется верификация email - проверяем ДО needsRoleSelection
  // чтобы избежать циклического редиректа на /role-selection
  if (requireEmailVerified && !user!.emailVerified) {
    console.log('[ProtectedRoute] Email verification check FAILED');
    console.log('[ProtectedRoute] requireEmailVerified:', requireEmailVerified);
    console.log('[ProtectedRoute] user.emailVerified:', user?.emailVerified);
    
    // Если мы на role-selection и email не верифицирован, возможно кэш устарел
    // Пробуем инвалидировать и подождать
    if (location.pathname === '/role-selection') {
      console.log('[ProtectedRoute] On /role-selection but email not verified - SPECIAL HANDLING');
      console.log('[ProtectedRoute] Forcing cache invalidation and refetch...');
      
      // Инвалидируем кэш
      queryClient.invalidateQueries({ queryKey: authQueryKeys.user() });
      
      // Показываем загрузку пока данные обновляются
      // НО: ограничим количество попыток чтобы избежать бесконечного цикла
      const attemptCount = parseInt(sessionStorage.getItem('roleSelectionAttempts') || '0');
      console.log('[ProtectedRoute] Attempt count:', attemptCount);
      
      if (attemptCount < 5) {
        sessionStorage.setItem('roleSelectionAttempts', String(attemptCount + 1));
        console.log('[ProtectedRoute] RETURN: LoadingSpinner (waiting for refetch)');
        return <LoadingSpinner />;
      } else {
        console.log('[ProtectedRoute] Max attempts reached, allowing through');
        sessionStorage.removeItem('roleSelectionAttempts');
        console.log('[ProtectedRoute] RETURN: Children (max attempts reached)');
        return <>{children}</>;
      }
    }
    
    console.log('[ProtectedRoute] RETURN: Navigate to /verify-email-pending');
    return <Navigate to="/verify-email-pending" replace />;
  }

  // Необходим выбор роли - НО не редиректим если:
  // 1. Уже на /role-selection
  // 2. Или на /verify-email-pending - пользователь должен сначала подтвердить email
  if (needsRoleSelection && 
      location.pathname !== '/role-selection' && 
      location.pathname !== '/verify-email-pending') {
    // Special case: if going to /dashboard from role-selection, allow it
    const fromRoleSelection = location.search.includes('fromRoleSelection=true');
    
    if (location.pathname === '/dashboard' && fromRoleSelection) {
      console.log('[ProtectedRoute] Allowing /dashboard despite pending role - came from role-selection');
    } else {
      console.log('[ProtectedRoute] RETURN: Navigate to /role-selection (needs role selection)');
      console.log('[ProtectedRoute] DEBUG - user?.role:', user?.role, 'needsRoleSelection:', needsRoleSelection);
      console.log('[ProtectedRoute] This redirect happens when role is still "pending" after selection!');
      return <Navigate to="/role-selection" replace />;
    }
  }

  // Проверка роли
  if (requiredRole) {
    const roles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!roles.includes(user!.role)) {
      console.log('[ProtectedRoute] RETURN: Navigate to /dashboard (role check failed)');
      return <Navigate to="/dashboard" replace />;
    }
  }

  console.log('[ProtectedRoute] RETURN: Children (all checks passed)');
  return <>{children}</>;
}
